/**
 * EndPublicationModal — confirme le retrait d'une publication active.
 *
 * Réutilisable cross-plateforme (S6 PR2-C Etsy ; S7+ Pinterest/Insta/TikTok).
 * Le caller passe le `platformLabel` pour l'affichage FR du contexte.
 *
 * `reason` = motif analytique propagé au scheduler :
 *   - `user_unpublish` : retrait volontaire
 *   - `sold_direct` : vendu hors plateforme (atelier, galerie)
 *
 * Les motifs `platform_takedown` / `expired` sont réservés au scheduler/
 * listing sync — pas exposés ici.
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { z } from 'zod'

import Button from '@/components/ui/Button'
import Field from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import Select from '@/components/ui/Select'
import type { EndPublicationReason } from '@/utils/api/work-publications'

const schema = z.object({
  reason: z.enum(['user_unpublish', 'sold_direct']),
})

type FormValues = z.infer<typeof schema>

interface EndPublicationModalProps {
  isOpen: boolean
  onClose: () => void
  platformLabel: string
  workTitle: string
  /** Caller exécute la mutation API + ferme + toast. La modale ne sait pas
   *  comment le faire — elle remonte juste le motif choisi. */
  onConfirm: (reason: EndPublicationReason) => void | Promise<void>
  isSubmitting?: boolean
}

export default function EndPublicationModal({
  isOpen,
  onClose,
  platformLabel,
  workTitle,
  onConfirm,
  isSubmitting = false,
}: EndPublicationModalProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { reason: 'user_unpublish' },
  })

  const handleSubmit = form.handleSubmit(async (values) => {
    await onConfirm(values.reason)
  })

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        if (!isSubmitting) {
          form.reset()
          onClose()
        }
      }}
      kicker={`${platformLabel} · Retirer une publication`}
      title={`Retirer « ${workTitle} » de ${platformLabel}`}
      size="form"
    >
      <FormProvider {...form}>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            <p className="ui-body">
              La publication sera retirée côté {platformLabel}. L'historique reste conservé en
              interne (audit + analytique).
            </p>
            <Field label="Motif du retrait" required>
              {(controlProps) => (
                <Select {...form.register('reason')} {...controlProps}>
                  <option value="user_unpublish">
                    Retrait volontaire (changement d'avis, archive)
                  </option>
                  <option value="sold_direct">Vendu hors {platformLabel} (atelier, galerie)</option>
                </Select>
              )}
            </Field>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="neutral" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button type="submit" variant="danger" disabled={isSubmitting}>
              {isSubmitting ? 'Retrait en cours…' : 'Retirer la publication'}
            </Button>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  )
}
